import { apiFetch } from '@/lib/api';

export interface Conversation {
  id: string;
  organization_id: string | null;
  user_id: string;
  title: string | null;
  provider: string;
  model: string;
  system_prompt: string | null;
  entity_type: string | null;
  entity_id: string | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: string;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  token_count: number | null;
  is_summary: boolean;
  metadata: Record<string, any> | null;
  created_at: string;
}

export interface CreateConversationInput {
  title?: string;
  provider: string;
  model: string;
  system_prompt?: string;
  entity_type?: string;
  entity_id?: string;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: { message: res.statusText } }));
    throw new Error(body?.error?.message || `Request failed: ${res.status}`);
  }
  const json = await res.json();
  return json.data;
}

export const conversationService = {
  async create(input: CreateConversationInput): Promise<Conversation> {
    const res = await apiFetch('/api/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return handleResponse<Conversation>(res);
  },

  async list(opts?: { limit?: number; offset?: number }): Promise<{ items: Conversation[]; total: number }> {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    const res = await apiFetch(`/api/conversations?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch conversations');
    const json = await res.json();
    return { items: json.data, total: json.meta?.total || 0 };
  },

  async get(id: string): Promise<Conversation & { messages: ConversationMessage[] }> {
    const res = await apiFetch(`/api/conversations/${id}`);
    return handleResponse<Conversation & { messages: ConversationMessage[] }>(res);
  },

  async sendMessage(id: string, message: string): Promise<{ text: string; usage: { inputTokens: number; outputTokens: number } }> {
    const res = await apiFetch(`/api/conversations/${id}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
    return handleResponse<{ text: string; usage: { inputTokens: number; outputTokens: number } }>(res);
  },

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`/api/conversations/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete conversation');
  },

  /**
   * Send a message and stream the response via SSE.
   * Returns a ReadableStream-based reader for progressive consumption.
   */
  async sendMessageStream(id: string, message: string, signal?: AbortSignal) {
    const res = await apiFetch(`/api/conversations/${id}/messages/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
      signal,
    });

    if (!res.ok) {
      const body = await res.text();
      throw new Error(body || `Stream failed: ${res.status}`);
    }

    return res;
  },
};
